/**
 * A brew put on the calendar again and again (Tyler, 2026-10-03: "the 28th of every month", "every
 * wednesday", "every other wednesday", "the x wednesday of each month (based on what wednesday/day is
 * selected)"). Each way of repeating is read from the first brew day, and gives every day from it
 * through an end day. Days are ISO dates, worked in UTC so no change of the clocks moves one.
 */

export type Repeat = "weekly" | "biweekly" | "monthlyNth" | "monthlyLast" | "monthlyDate";

export interface RepeatOption {
  id: Repeat;
  label: string;
}

/** At most this many days in one go, as the API takes. */
export const MAX_DAYS = 370;

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const NTH = ["first", "second", "third", "fourth"];

const parse = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
};
const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysIn = (year: number, month: number) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

/** 1st, 2nd, 3rd, 4th ... 21st, 22nd, 23rd, 31st. */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] ?? "th"}`;
}

export function addDays(day: string, n: number): string {
  const d = parse(day);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
}

/** The same day `n` months on, or the month's last day when it has no such day. */
export function monthsLater(day: string, n: number): string {
  const d = parse(day);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + n;
  const target = new Date(Date.UTC(year, month, 1));
  const last = daysIn(target.getUTCFullYear(), target.getUTCMonth());
  return iso(
    new Date(
      Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d.getUTCDate(), last)),
    ),
  );
}

/** Which of its weekday in its month a day is: the 28th of October 2026 is the 4th Wednesday. */
const nthOf = (d: Date) => Math.ceil(d.getUTCDate() / 7);
const isLastOf = (d: Date) => d.getUTCDate() + 7 > daysIn(d.getUTCFullYear(), d.getUTCMonth());

/** The ways a brew on `start` can repeat, named for that day. */
export function repeatsFor(start: string): RepeatOption[] {
  const d = parse(start);
  const weekday = WEEKDAYS[d.getUTCDay()] as string;
  const date = d.getUTCDate();
  const out: RepeatOption[] = [
    { id: "weekly", label: `Every ${weekday}` },
    { id: "biweekly", label: `Every other ${weekday}` },
  ];
  // A fifth one is not in every month, so the fifth is offered only as the last.
  if (nthOf(d) <= 4) {
    out.push({
      id: "monthlyNth",
      label: `The ${NTH[nthOf(d) - 1]} ${weekday} of every month`,
    });
  }
  if (isLastOf(d)) out.push({ id: "monthlyLast", label: `The last ${weekday} of every month` });
  out.push({
    id: "monthlyDate",
    label:
      date > 28
        ? `The ${ordinal(date)} of every month, or its last day`
        : `The ${ordinal(date)} of every month`,
  });
  return out;
}

/** Each day from `start` through `until` that a brew repeating so falls on, `start` first. */
export function datesFor(start: string, repeat: Repeat, until: string): string[] {
  if (until < start) return [];
  const first = parse(start);
  const out: string[] = [];
  const push = (d: Date) => {
    const day = iso(d);
    if (day >= start && day <= until && out.length < MAX_DAYS) out.push(day);
  };
  if (repeat === "weekly" || repeat === "biweekly") {
    const step = repeat === "weekly" ? 7 : 14;
    for (let day = start; day <= until && out.length < MAX_DAYS; day = addDays(day, step)) {
      out.push(day);
    }
    return out;
  }
  const weekday = first.getUTCDay();
  const nth = nthOf(first);
  for (let m = 0; out.length < MAX_DAYS; m++) {
    const monthStart = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + m, 1));
    if (iso(monthStart) > until) break;
    const year = monthStart.getUTCFullYear();
    const month = monthStart.getUTCMonth();
    const last = daysIn(year, month);
    if (repeat === "monthlyDate") {
      push(new Date(Date.UTC(year, month, Math.min(first.getUTCDate(), last))));
    } else if (repeat === "monthlyNth") {
      const firstOfKind = 1 + ((weekday - monthStart.getUTCDay() + 7) % 7);
      push(new Date(Date.UTC(year, month, firstOfKind + (nth - 1) * 7)));
    } else {
      const lastDay = new Date(Date.UTC(year, month, last));
      push(new Date(Date.UTC(year, month, last - ((lastDay.getUTCDay() - weekday + 7) % 7))));
    }
  }
  return out;
}

/** How a series repeats, in words: its rule as offered for its first day, or days picked one by one. */
export function repeatLabel(repeat: Repeat | "days", firstDay: string): string {
  if (repeat === "days") return "On days picked one by one";
  return repeatsFor(firstDay).find((r) => r.id === repeat)?.label ?? "Repeating";
}
